import { QUESTS_API_URL } from "../config/questsApi";
import { GAME_LEVEL_COUNT } from "../constants/gameLevels";
import { dispatchNotificationsChanged } from "./levelHistoryApi";
import { processQuestCompletionSideEffects } from "./questCompletionHandler";

export const QUEST_PROGRESS_CHANGED_EVENT = "vocabularium-quest-progress-changed";

function dispatchQuestProgressChanged() {
  window.dispatchEvent(new CustomEvent(QUEST_PROGRESS_CHANGED_EVENT));
}

export function notifyQuestProgressChanged() {
  dispatchQuestProgressChanged();
}

async function postQuestEvents(events) {
  if (!QUESTS_API_URL) return null;

  const res = await fetch(`${QUESTS_API_URL}/progress`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ events }),
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(json.message || "Impossible d'enregistrer la progression des quêtes.");
  }

  dispatchQuestProgressChanged();
  dispatchNotificationsChanged();
  await processQuestCompletionSideEffects(json);
  return json;
}

export async function reportQuestCapture(count = 1, wordCounts = null) {
  try {
    return await postQuestEvents([
      {
        type: "capture",
        count,
        ...(wordCounts || {}),
      },
    ]);
  } catch (err) {
    console.warn("Quêtes — capture :", err.message);
    return null;
  }
}

export async function reportQuestMemorySession({ score, totalQuestions }) {
  try {
    return await postQuestEvents([
      {
        type: "memory_session",
        score,
        totalQuestions,
      },
    ]);
  } catch (err) {
    console.warn("Quêtes — mémoire :", err.message);
    return null;
  }
}

export async function reportQuestCommonGem({ gemId, source = "chest" }) {
  if (!gemId) return null;

  try {
    return await postQuestEvents([
      {
        type: "common_gem_obtained",
        gemId,
        source,
      },
    ]);
  } catch (err) {
    console.warn("Quêtes — pierre commune :", err.message);
    return null;
  }
}

export async function reportQuestGameFinished(payload) {
  try {
    return await postQuestEvents([
      {
        type: "game_finished",
        ...payload,
      },
    ]);
  } catch (err) {
    console.warn("Quêtes — partie :", err.message);
    return null;
  }
}

export async function reportQuestTrainingSession({ validated = false } = {}) {
  try {
    return await postQuestEvents([
      {
        type: "training_session",
        validated: Boolean(validated),
      },
    ]);
  } catch (err) {
    console.warn("Quêtes — session d'entraînement :", err.message);
    return null;
  }
}

export async function reportQuestTestNextLevelPlay({ level, maxUnlockedLevel } = {}) {
  const playedLevel = Number(level);
  const currentLevel = Number(maxUnlockedLevel);
  if (
    !Number.isInteger(playedLevel) ||
    !Number.isInteger(currentLevel) ||
    playedLevel < 1 ||
    playedLevel > GAME_LEVEL_COUNT ||
    currentLevel < 1 ||
    currentLevel > GAME_LEVEL_COUNT
  ) {
    return null;
  }

  try {
    return await postQuestEvents([
      {
        type: "test_next_level_play",
        level: playedLevel,
        maxUnlockedLevel: currentLevel,
      },
    ]);
  } catch (err) {
    console.warn("Quêtes — partie mode T :", err.message);
    return null;
  }
}

export async function reportQuestWordCounts(wordCounts) {
  if (!wordCounts || typeof wordCounts !== "object") return null;
  const capturedWordCount = Number(wordCounts.capturedWordCount);
  const masteredWordCount = Number(wordCounts.masteredWordCount);
  const payload = { type: "word_counts" };
  if (Number.isFinite(capturedWordCount)) {
    payload.capturedWordCount = Math.max(0, Math.round(capturedWordCount));
  }
  if (Number.isFinite(masteredWordCount)) {
    payload.masteredWordCount = Math.max(0, Math.round(masteredWordCount));
  }
  if (payload.capturedWordCount == null && payload.masteredWordCount == null) {
    return null;
  }

  try {
    return await postQuestEvents([payload]);
  } catch (err) {
    console.warn("Quêtes — mots du registre :", err.message);
    return null;
  }
}
